import { FC, MouseEvent, PointerEvent, useMemo, useState } from 'react'
import { CloseOutlined, UserAddOutlined } from '@ant-design/icons'
import type { MenuProps } from 'antd'
import Button from '@UI/New/Button'
import CascadingDropdown from '@UI/New/CascadingDropdown'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useFullLeadModalAccess } from '../../hooks/useFullLeadModalAccess'
import { useLeadRefetch } from '../../LeadRefetchContext'
import type { LeadProfileLead } from '../../LeadProfileModal.types'
import type { CrmLeadParent } from '@/api/types/crm/crmLeadParent.types'
import { handlerDetachParent } from './detachParentModalFn'
import classes from './index.module.css'

interface TabBindingParentsProps {
  lead: LeadProfileLead
  onOpenAddParent: () => void
  onOpenParentAccount: (parentId: number) => void
  onHistoryRefresh?: () => void
}

const formatParentMeta = (parent: CrmLeadParent): string => {
  return [parent.phone, parent.email].filter(Boolean).join(' · ')
}

const TabBindingParents: FC<TabBindingParentsProps> = ({
  lead,
  onOpenAddParent,
  onOpenParentAccount,
  onHistoryRefresh,
}) => {
  const addAlert = useAddAlert()
  const access = useFullLeadModalAccess()
  const { patchLead } = useLeadRefetch()
  const [dropdownOpen, setDropdownOpen] = useState(false)

  /** Единый список: get-lead / crm-lead-parent (direct + student). */
  const parents = useMemo(() => {
    const list = Array.isArray(lead.parents) ? lead.parents : []
    return list.filter(
      (item) => item?.id != null && Number.isFinite(Number(item.id)),
    )
  }, [lead.parents])

  const canManage = access.canBindUnbindStudent

  if (!canManage && parents.length === 0) {
    return null
  }

  const applyParents = (nextParents: CrmLeadParent[]) => {
    patchLead({ parents: nextParents })
    onHistoryRefresh?.()
  }

  const openDetachConfirm = (parent: CrmLeadParent) => {
    if (!canManage || !lead.id) {
      return
    }

    handlerDetachParent({
      leadId: lead.id,
      parent,
      hasBoundStudent:
        lead.user_id != null && Number.isFinite(Number(lead.user_id)),
      addAlert,
      onSuccess: (nextParents) => {
        applyParents(nextParents)
        setDropdownOpen(false)
      },
    })
  }

  const menuItems = [
    ...(parents.length === 0
      ? [
          {
            key: 'parents-empty',
            disabled: true,
            label: (
              <span className={classes.emptyHint}>Родители не привязаны</span>
            ),
          },
        ]
      : parents.map((parent) => {
          const meta = formatParentMeta(parent)

          return {
            key: `parent-${parent.id}`,
            label: (
              <div className={classes.optionRow}>
                <div className={classes.optionMain}>
                  <span className={classes.optionName}>{parent.name}</span>
                  {meta ? (
                    <span className={classes.optionMeta}>{meta}</span>
                  ) : null}
                </div>
                {canManage ? (
                  <span
                    role="button"
                    tabIndex={0}
                    className={classes.detachButton}
                    aria-label={`Отвязать родителя ${parent.name}`}
                    onPointerDown={(event: PointerEvent<HTMLSpanElement>) => {
                      event.preventDefault()
                      event.stopPropagation()
                    }}
                    onMouseDown={(event: MouseEvent<HTMLSpanElement>) => {
                      event.preventDefault()
                      event.stopPropagation()
                    }}
                    onClick={(event: MouseEvent<HTMLSpanElement>) => {
                      event.preventDefault()
                      event.stopPropagation()
                      openDetachConfirm(parent)
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        event.stopPropagation()
                        openDetachConfirm(parent)
                      }
                    }}
                  >
                    <CloseOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </span>
                ) : null}
              </div>
            ),
            onClick: ({
              domEvent,
            }: {
              domEvent: { stopPropagation: () => void }
            }) => {
              domEvent.stopPropagation()
              setDropdownOpen(false)
              onOpenParentAccount(parent.id)
            },
          }
        })),
    ...(canManage
      ? [
          { type: 'divider' as const },
          {
            key: 'add-parent',
            label: (
              <span className={classes.addOption}>
                <UserAddOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                Добавить ЛК родителя
              </span>
            ),
            onClick: () => {
              setDropdownOpen(false)
              onOpenAddParent()
            },
          },
        ]
      : []),
  ] as MenuProps['items']

  return (
    <div className={classes.wrapper}>
      <CascadingDropdown
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        items={menuItems}
        placement="bottomRight"
        overlayClassName={classes.dropdown}
        rootDataAttr="data-crm-lead-parents-dropdown"
      >
        <Button
          type="button"
          color="primary"
          className={classes.triggerButton}
          rightContent={
            <span className={classes.countBadge}>{parents.length}</span>
          }
        >
          Родители
        </Button>
      </CascadingDropdown>
    </div>
  )
}

export default TabBindingParents
